import { useEffect, useState } from 'react'
import { withTranslation } from 'react-i18next'

import { useExternalDevices } from '@/lib/hooks'
import { cancelCurrentPayment, startPayment } from '@/lib/terminal'
import Button from '@/ui/Button'
import { LoaderSpinner } from '@/ui/Loader/Loader'

import useCheckout from '../../hooks/useCheckout'
import CheckoutStep from '../CheckoutStep'
import { TerminalActions, TerminalLoader, TerminalWrapper } from './Terminal.styles'

const Terminal = ({ t }) => {
  const { data: externalDevices, isSuccess } = useExternalDevices()
  const terminal = externalDevices?.find(o => o.type === 'PAYMENT_TERMINAL')
  const { createOrder, cartTotal, setCurrentStep } = useCheckout()
  const [paymentState, setPaymentState] = useState('PENDING')
  const [terminalState, setTerminalState] = useState(null)
  const [terminalCode, setTerminalCode] = useState(null)
  const [paymentMessage, setPaymentMessage] = useState('')
  const [showLoader, setShowLoader] = useState(false)
  const [errorCount, setErrorCount] = useState(0)
  const [customMessage, setCustomMessage] = useState('')
  const invoiceId = '12345'

  const initPayment = () => {
    setPaymentState('DEFAULT')

    startPayment(terminal, cartTotal, invoiceId)
      .then(response => {
        setTerminalState(response)

        if (response !== 'PAYMENT_STARTED') {
          setPaymentState('ERROR')
          setShowLoader(false)
          setErrorCount(prev => prev + 1)
        } else {
          setPaymentState('PENDING')
          setShowLoader(true)
          setErrorCount(0)
        }
      })
      .catch(() => {
        // NOTE: For Dev Purposes
        // setPaymentState('SUCCESS')
        // next()

        setPaymentState('ERROR')
        setShowLoader(false)
        setErrorCount(prev => prev + 1)
      })
  }

  const cancelPayment = () => {
    cancelCurrentPayment().catch(() => {})
  }

  useEffect(() => {
    if (window.BtApp) {
      window.BtApp.paymentStatus = (defaultMessage) => {
        console.log(`[TERMINAL] paymentStatus:`, { defaultMessage })

        setPaymentState('PENDING')
        setShowLoader(true)
        setCustomMessage(defaultMessage || '')
      }

      window.BtApp.paymentFinished = (response, defaultMessage, transactionId) => {
        console.log(`[TERMINAL] paymentFinished:`, { response, defaultMessage, transactionId })

        setTerminalCode(response)
        setCustomMessage(defaultMessage || '')
        setPaymentMessage(defaultMessage || '')

        if (response === 0 || response === '0') {
          setPaymentState('SUCCESS')
          const orderPromise = createOrder()

          if (orderPromise && typeof orderPromise.unwrap === 'function') {
            orderPromise
              .unwrap()
              .then(() => {
                setCurrentStep('SUMMARY')
              })
              .catch(err => {
                console.error('[TERMINAL] createOrder error:', err)
                setPaymentState('ERROR')
                setShowLoader(false)
              })
          } else if (orderPromise && typeof orderPromise.then === 'function') {
            orderPromise
              .then(() => {
                setCurrentStep('SUMMARY')
              })
              .catch(err => {
                console.error('[TERMINAL] createOrder error:', err)
                setPaymentState('ERROR')
                setShowLoader(false)
              })
          } else {
            console.error('[TERMINAL] createOrder did not return a promise')
            setPaymentState('ERROR')
            setShowLoader(false)
          }
        } else {
          setPaymentState('ERROR')
          setShowLoader(false)
          setErrorCount(prev => prev + 1)
        }
      }
    }

    if (terminal) {
      initPayment()
    } else {
      setPaymentState('ERROR')
    }

    return () => {
      cancelPayment()
    }
  }, [])

  const Pending = () => {
    let message = t('terminalDescription')

    if (customMessage) {
      message = customMessage
    } else if (terminalCode && terminalCode !== 0 && terminalCode !== '0') {
      message = t(`terminalState.${terminalCode}`)
    }

    return (
      <>
        <TerminalLoader show={showLoader}>
          <LoaderSpinner />
        </TerminalLoader>
        <h1>{t('terminalTitle')}</h1>
        <p>{message}</p>
      </>
    )
  }

  const Error = () => {
    let message = t('errorOccurred')

    if (!terminal) {
      message = t('terminalError.description.noTerminal')
    } else if (terminalState === 'NO_CONNECTION') {
      message = t('payment.error.description.noConnection', { ip: terminal.ip, port: terminal.port })
    } else if (terminalState === 'AMOUNT_INVALID') {
      message = t('payment.error.description.amountInvalid')
    } else if (terminalState === 'INTERNAL_ERROR') {
      message = t('payment.error.description.internal')
    } else {
      if (errorCount <= 3) {
        message = t('payment.error.description.repeat')
      } else {
        message = t('payment.error.description.cancel')
      }
    }

    return (
      <>
        <TerminalLoader show={showLoader}>
          <LoaderSpinner />
        </TerminalLoader>
        <h1>{t('terminalError.title')}</h1>
        <p>{message}</p>
        <TerminalActions>
          <Button variant="outline" size="lg" onClick={() => setCurrentStep('PAYMENT')}>
            {t('back')}
          </Button>
          <Button size="lg" onClick={() => initPayment()}>
            {t('retry')}
          </Button>
        </TerminalActions>
      </>
    )
  }

  return (
    <CheckoutStep>
      <TerminalWrapper>
        {paymentState === 'PENDING' && <Pending />}
        {paymentState === 'ERROR' && <Error />}
      </TerminalWrapper>
    </CheckoutStep>
  )
}

export default withTranslation()(Terminal)
